import _ from 'lodash';
import { observer } from 'mobx-react-lite';
import { useEffect, useRef } from 'react';
import { twMerge } from 'tailwind-merge';
import modalStore, { ModalOption } from '@client/stores/modalStore';
import AlertModal from './AlertModal';
import ConfirmModal from './ConfirmModal';
import CustomModal from './CustomModal';
import LoginModal from './LoginModal';
import MenuModal from './MenuModal';
import SelectModal from './SelectModal';
import { ModalActionButton, ModalCloseXButton } from './ModalButton';
import ErrorModal from './ErrorModal';

/**
 * InternalModal - 순수 라우터 역할
 * - 타입별로 적절한 모달 컴포넌트를 렌더링
 */
const InternalModal = observer(({ modalId }: { modalId: string }) => {
    const modalItem = modalStore.modalMap.get(modalId)?.modalItem;

    if (!modalItem) {
        return null;
    }

    switch (modalItem.option.type) {
        case 'alert':
            return <AlertModal modalId={modalId} option={modalItem.option} />;
        case 'confirm':
            return <ConfirmModal modalId={modalId} option={modalItem.option} />;
        case 'login':
            return <LoginModal modalId={modalId} option={modalItem.option} />;
        case 'custom':
            return <CustomModal modalId={modalId} option={modalItem.option} />;
        case 'select':
            return <SelectModal modalId={modalId} option={modalItem.option} />;
        case 'menu':
            return <MenuModal modalId={modalId} option={modalItem.option} />;
        case 'error':
            return <ErrorModal modalId={modalId} option={modalItem.option} />;
        default:
            return null;
    }
});

/**
 * 모달 컨테이너
 * - 루트에 한 번만 붙여둔다
 * - alert, confirm, login, custom, select, menu, error 타입 처리
 * - 앱별 전용 타입(guide, stock 등)이 필요하면 별도 컨테이너를 사용
 */
export const ModalContainer = observer(() => {
    return (
        <div className="invisible fixed h-0 w-0">
            {_.map(Array.from(modalStore.modalMap.entries()), ([modalId, obj]) => {
                const modalItem = obj.modalItem;

                if (modalItem?.state === 'opened' || modalItem?.state === 'closing') {
                    return <InternalModal key={modalId} modalId={modalId} />;
                }
                return null;
            })}
        </div>
    );
});

/**
 * Modal 컴포넌트 라이브러리
 * - 각 모달에서 조합하여 사용
 */

const Dialog = observer(
    ({
        modalId,
        children,
        className,
    }: {
        modalId: string;
        children: React.ReactNode;
        className?: string;
    }) => {
        const dialogRef = useRef<HTMLDialogElement>(null);
        const modalItem = modalStore.modalMap.get(modalId)?.modalItem;
        const option = modalItem?.option;

        useEffect(() => {
            const dialog = dialogRef.current;
            if (!dialog) {
                return;
            }

            modalStore.setModalElement(modalId, dialog);

            _.delay(() => {
                dialog.showModal();
                modalItem?.option.onOpen?.({ modalId });
            }, 50);

            const onClose = () => {
                modalStore.prepareClose(modalId);

                const returnValue = modalItem?.returnValue;
                if (modalItem?.onClose) {
                    modalItem.onClose(returnValue);
                }

                _.delay(() => {
                    modalStore.completeClose(modalId);
                }, 500);
            };

            const onCancel = (e: Event) => {
                if (modalItem?.option.preventClose) {
                    e.preventDefault();
                }
            };

            dialog.addEventListener('close', onClose);
            dialog.addEventListener('cancel', onCancel);

            return () => {
                dialog.removeEventListener('close', onClose);
                dialog.removeEventListener('cancel', onCancel);
            };
        }, [modalId]);

        return (
            <dialog
                ref={dialogRef}
                className={twMerge(
                    'modal select-none',
                    'modal-bottom',
                    'sm:modal-middle',
                    className,
                )}
            >
                <div
                    className={twMerge(
                        'modal-box container min-w-0 overflow-visible px-0 pt-6 pb-5',
                        option?.maxWidth
                            ? 'sm:max-w-[min(calc(100dvw-40px),var(--modal-max-width))]'
                            : 'sm:max-w-[450px]',
                    )}
                    style={
                        option?.maxWidth
                            ? ({
                                  ['--modal-max-width' as const]: option.maxWidth,
                              } as React.CSSProperties)
                            : undefined
                    }
                >
                    {option?.title && <Title option={option} />}
                    {children}
                    {option?.showCloseButton && <ModalCloseXButton />}
                </div>
                {option?.backdrop && (
                    <form method="dialog" className="modal-backdrop">
                        {!option?.preventClose && <button>닫기</button>}
                    </form>
                )}
            </dialog>
        );
    },
);

const Title = observer(({ option }: { option: ModalOption }) => {
    if (!option.title) {
        return null;
    }

    return (
        <h3
            className={
                option.titleAlign === 'center'
                    ? 'mx-5 mb-4 w-full shrink-0 text-center text-xl font-bold'
                    : 'mx-5 mb-4 shrink-0 text-lg font-bold'
            }
        >
            {option.title}
        </h3>
    );
});

const BodyScroll = observer(
    ({ children, className }: { children: React.ReactNode; className?: string }) => {
        return (
            <div
                className={twMerge('max-h-[calc(90vh-8rem)] overflow-auto px-5', className)}
                style={{
                    WebkitOverflowScrolling: 'touch',
                    overscrollBehaviorY: 'contain',
                }}
            >
                {children}
            </div>
        );
    },
);

/**
 * Modal 컴포넌트 - Compound Component 패턴
 */
export const Modal = Object.assign(
    {},
    {
        Dialog,
        Title,
        BodyScroll,
        ActionButton: ModalActionButton,
        CloseXButton: ModalCloseXButton,
    },
);
